import { PosIntegrationType } from '../enums/PosIntegrationType';
import { WasteType } from '../enums/WasteType';

/**
 * PosIntegration is stored as a composed "integration[-wasteType][-identifier]" string, so no fixed
 * allowed-value list can describe it and the generic scalar editor cannot edit it safely. These
 * helpers reproduce the composition rules the previous specialist screen used, unchanged.
 */
export interface PosIntegrationParts {
  integration: string;
  wasteType: string;
  identifier: string;
}

export const emptyPosIntegrationParts: PosIntegrationParts = { integration: '', wasteType: '', identifier: '' };

export const usesWasteType = (integration: string): boolean =>
  integration === PosIntegrationType.RTS || integration === PosIntegrationType.Cloud;

export const parsePosIntegrationValue = (value?: string): PosIntegrationParts => {
  if (!value) return emptyPosIntegrationParts;

  const [integration, second, third] = value.split('-');

  return usesWasteType(integration)
    ? { integration, wasteType: second ?? '', identifier: third ?? '' }
    : { integration, wasteType: '', identifier: second ?? '' };
};

/**
 * Returns undefined when the parts do not yet describe a complete value, so the caller can keep the
 * stored value rather than writing a partial one. This mirrors the previous screen, which fell back
 * to the current value in exactly these cases.
 */
export const composePosIntegrationValue = (parts: PosIntegrationParts): string | undefined => {
  const { integration, wasteType, identifier } = parts;

  if (!integration) return '';

  if (!usesWasteType(integration)) {
    if (integration === PosIntegrationType.Standard || integration === PosIntegrationType.Lightspeed) return integration;
    return identifier ? `${integration}-${identifier}` : undefined;
  }

  if (!wasteType) return undefined;
  if (wasteType === WasteType.GL && !identifier) return undefined;

  const ownWasteType = integration === PosIntegrationType.RTS ? WasteType.RTS : WasteType.Cloud;

  return identifier && wasteType !== ownWasteType
    ? `${integration}-${wasteType}-${identifier}`
    : `${integration}-${wasteType}`;
};
